// =============================================================================
// TASK-4803 — Smart Job Builder: what the form does with stops the builder
// found in the company's past jobs. Pure functions, no DOM, no Firestore — kept
// out of dispatch.js so the Add Job modal redesign (TASK-4695) and this change
// touch different code, and so tests/task4803-job-builder-history.unit.cjs can
// run them in node.
//
// The server (functions/src/index.js _jobBuilderVerifyStops) has ALREADY
// dropped every stop whose coordinate the history tool did not return, and
// supplies the name + provenance from the tool result. This file only orders
// them and says, in plain words, where each came from. It never creates a job:
// the dispatcher still presses Create.
// =============================================================================

const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const finite = (v) => typeof v === 'number' && Number.isFinite(v);

function verifiedStops(parsed) {
  const list = Array.isArray(parsed && parsed.stops) ? parsed.stops : [];
  return list.filter((s) => s && finite(s.lat) && finite(s.lng)
    && Math.abs(s.lat) <= 90 && Math.abs(s.lng) <= 180 && !(s.lat === 0 && s.lng === 0));
}

/**
 * The waypoint rows to add, in route order: typed origin → history stops (in
 * the builder's order) → typed destination. `generic: true` marks a made-up label ("Pickup") the
 * caller may blank if it collides with a saved-location name.
 * @returns {{name:string,url:string,lat:number|null,lng:number|null,generic:boolean}[]}
 */
export function planJobBuilderWaypoints(parsed) {
  const p = parsed || {};
  const stops = verifiedStops(p);
  // The same Google Maps ?q= link the server builds — dispatch.js's
  // extractCoordsFromUrl reads it back, so the row is pinned with no geocode.
  const row = (s) => ({ name: String(s.name || ''), url: `https://www.google.com/maps?q=${s.lat},${s.lng}`, lat: s.lat, lng: s.lng, generic: false });
  // COD (Edge Case Hunter): keep the model's stop order — pickup, scale,
  // second pickup must stay in that order. The builder is told to null a typed
  // origin/location that a stop already covers, so a non-null one is a
  // DIFFERENT place ("from Williston, pick up at the Loenbro yard") and is kept:
  // typed origin first, typed destination last.
  const out = [];
  if (p.originLocation) out.push({ name: 'Pickup', url: String(p.originLocation), lat: null, lng: null, generic: true });
  out.push(...stops.map(row));
  if (p.location) {
    const label = (p.originLocation || stops.length) ? 'Drop off' : (p.wellName || 'Job site');
    out.push({ name: String(label), url: String(p.location), lat: null, lng: null, generic: true });
  }
  return out;
}

/**
 * The note shown under the builder after a fill: where each stop came from,
 * which past stops have no coordinates on file, anything the server refused,
 * and the builder's own question. '' when there is nothing to say.
 */
export function jobBuilderHistoryNoteHtml(parsed) {
  const p = parsed || {};
  const stops = verifiedStops(p);
  const noCoords = Array.isArray(p.stopsWithoutCoords) ? p.stopsWithoutCoords.filter((n) => typeof n === 'string' && n) : [];
  const dropped = Number.isInteger(p.stopsDropped) && p.stopsDropped > 0 ? p.stopsDropped : 0;
  const followUp = typeof p.followUp === 'string' ? p.followUp.trim() : '';
  if (!stops.length && !noCoords.length && !dropped && !followUp) return '';

  let html = '<div style="display:flex;align-items:flex-start;gap:10px;">'
    + '<span class="material-icons" style="color:#2563EB;font-size:20px;flex-shrink:0;margin-top:1px;">history</span><div style="min-width:0;">';
  if (stops.length) {
    html += '<strong>Destinations filled from your past jobs</strong> — check them before you save.'
      + '<ul style="margin:6px 0 0;padding-left:18px;">'
      + stops.map((s) => `<li><strong>${esc(s.name)}</strong> <span style="color:#475569;">(${esc(s.source || 'past job')})</span></li>`).join('')
      + '</ul>';
  }
  if (noCoords.length) {
    html += `<div style="margin-top:6px;"><strong>No coordinates on file:</strong> ${noCoords.map(esc).join(', ')}. Add a map link or address for ${noCoords.length === 1 ? 'that destination' : 'those destinations'} by hand.</div>`;
  }
  if (dropped) {
    html += `<div style="margin-top:6px;color:#92400E;">${dropped === 1 ? 'One suggested destination' : `${dropped} suggested destinations`} did not match anything in your past jobs and ${dropped === 1 ? 'was' : 'were'} left out — add ${dropped === 1 ? 'that destination' : 'those destinations'} by hand before you save.</div>`;
  }
  if (followUp) {
    html += `<div style="margin-top:6px;"><strong>Job Builder:</strong> ${esc(followUp)}</div>`;
  }
  html += '</div></div>';
  return html;
}

// =============================================================================
// TASK-4803 S3a — the usual crew and equipment, as SUGGESTIONS.
// `parsed.crewSuggestions` comes straight from the server's findPastJobCrew
// tool (the model never saw these ids). Nothing here selects anything: each
// suggestion is a button the dispatcher taps to add, and they still press
// Create. No availability is shown — a driver or truck on two jobs the same day
// is normal, so a suggestion never says "busy".
//
// resolve(section, id) → { label } for a row that is in this form's picker and
// NOT already ticked; null otherwise (not in the roster, hidden from dispatch,
// or already on the job). So an id the browser does not know is never shown.
// =============================================================================
const CREW_SECTIONS = [
  { key: 'people', section: 'drivers', title: 'Crew' },
  { key: 'trucks', section: 'trucks', title: 'Vehicles' },
  { key: 'trailers', section: 'trailers', title: 'Trailers' },
];

export function planJobBuilderCrewSuggestions(parsed, resolve) {
  const cs = parsed && parsed.crewSuggestions;
  if (!cs || typeof cs !== 'object' || typeof resolve !== 'function') return [];
  const out = [];
  for (const { key, section, title } of CREW_SECTIONS) {
    const list = Array.isArray(cs[key]) ? cs[key] : [];
    const items = [];
    // The server sends up to 12 per kind; ids this roster does not know (incl.
    // legacy name strings) are dropped here, THEN the first 5 are shown.
    for (const s of list.slice(0, 12)) {
      if (items.length >= 5) break;
      if (!s || typeof s.id !== 'string' || !s.id) continue;
      const hit = resolve(section, s.id);
      if (!hit || !hit.label) continue;
      items.push({ id: s.id, label: String(hit.label), jobs: Number.isInteger(s.jobs) && s.jobs > 0 ? s.jobs : 1, lastUsed: typeof s.lastUsed === 'string' ? s.lastUsed : '' });
    }
    if (items.length) out.push({ section, title, items });
  }
  return out;
}

export function jobBuilderCrewSuggestionsHtml(parsed, resolve) {
  const groups = planJobBuilderCrewSuggestions(parsed, resolve);
  if (!groups.length) return '';
  const cs = parsed.crewSuggestions;
  const n = Number.isInteger(cs.jobsMatched) ? cs.jobsMatched : 0;
  const basedOn = (Array.isArray(cs.basedOn) ? cs.basedOn : []).slice(0, 3)
    .map((j) => [j && j.jobDate, j && j.customer, j && j.title].filter(Boolean).map(esc).join(' — '))
    .filter(Boolean);
  let html = '<div style="display:flex;align-items:flex-start;gap:10px;">'
    + '<span class="material-icons" style="color:#2563EB;font-size:20px;flex-shrink:0;margin-top:1px;">groups</span><div style="min-width:0;flex:1;">'
    + `<strong>Usually on jobs like this</strong> — from ${n === 1 ? '1 past job' : `${n} past jobs`}. Tap to add; nothing is added until you do.`;
  if (basedOn.length) {
    html += `<div style="margin-top:4px;color:#475569;font-size:0.85rem;">${basedOn.join('; ')}${n > basedOn.length ? '; …' : ''}</div>`;
  }
  if (cs.matchedOnlySomeWords) {
    html += '<div style="margin-top:4px;color:#92400E;font-size:0.85rem;">No past job matched every word you typed — these come from the closest matches.</div>';
  }
  for (const g of groups) {
    html += `<div style="margin-top:8px;"><div style="font-size:0.8rem;font-weight:600;color:var(--text-secondary);margin-bottom:4px;">${esc(g.title)}</div><div style="display:flex;flex-wrap:wrap;gap:6px;">`;
    html += g.items.map((it) => `<button type="button" class="btn btn-small btn-secondary" data-jb-suggest="${esc(g.section)}" data-jb-id="${esc(it.id)}" title="${esc(`On ${it.jobs} of these past jobs${it.lastUsed ? `, last ${it.lastUsed}` : ''}`)}">`
      + `<span class="material-icons" style="font-size:16px;vertical-align:middle;">add</span> ${esc(it.label)} <span style="color:#475569;font-weight:400;">(${it.jobs}/${n || it.jobs})</span></button>`).join('');
    html += '</div></div>';
  }
  html += '</div></div>';
  return html;
}
